/** Support queue — XS Freshservice work items, department-triaged.
 *  Separate world from projects: nothing here touches portfolio counts,
 *  the exec Gantt, or capacity. See backend/app/routers/support_queue.py. */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";

export interface SupportQueueItem {
  id: number;
  fs_ticket_id: number;
  subject: string;
  /** Who submitted the ticket. */
  requester: string | null;
  /** Set only when the request was raised on someone else's behalf. */
  requested_for: string | null;
  fs_created_at: string | null;
  /** Requester's Freshservice department (display-only context);
   *  distinct from `department`, the hand-picked planner routing. */
  fs_department: string | null;
  department: string | null;
  rank: number | null;
  jira_key: string | null;
  done_manual: boolean;
  /** Set when promoted to a project (2026-09-29). */
  promoted_project_id?: string | null;
  /** Which door it came through (2026-10-01). */
  source_group?: "application_support" | "project_management";
  request_type?: string | null;
  /** Freshservice priority 1..4 (Low..Urgent). */
  fs_priority?: number | null;
  /** PMO's call — the "most impactful" signal. */
  impact?: "high" | "medium" | "low" | null;
  /** Set when a board request was sent to the queue. */
  source_project_id?: string | null;
  jira_status: string | null;
  jira_status_category: string | null;
  /** Mirrored from Freshservice approvals: approved | pending | rejected
   *  | none ('none' = never requested, distinct from pending). */
  approval_state: "approved" | "pending" | "rejected" | "none";
  /** Planner-side approval (set when the request came FROM the approver,
   *  so Freshservice can never show one). Wins over approval_state. */
  approval_override: "approved" | null;
  approval_override_by: string | null;
  /** What the UI should act on: override if set, else the FS state. */
  approval_state_effective: "approved" | "pending" | "rejected" | "none";
  approver_name: string | null;
  approval_updated_at: string | null;
  closed_at: string | null;
  closed_reason: string | null;
  /** Finished, however it finished (FS left the queue / Jira done / manual). */
  is_closed: boolean;
}

export function useSupportQueue(enabled = true) {
  return useQuery<SupportQueueItem[]>({
    queryKey: ["support-queue"],
    queryFn: () => api.get("/support-queue").then((r) => r.data.items),
    enabled,
  });
}

export function useSyncSupportQueue() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post("/support-queue/sync").then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["support-queue"] }),
  });
}

export function useUpdateSupportQueueItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...fields }: { id: number } & Partial<SupportQueueItem>) =>
      api.patch(`/support-queue/${id}`, fields).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["support-queue"] }),
  });
}

/** Promote an enhancement to a project — lands in New / unscoped on the board. */
export function usePromoteSupportQueueItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.post(`/support-queue/${id}/promote`).then((r) => r.data as { project_id: string; name: string }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["support-queue"] });
      qc.invalidateQueries({ queryKey: ["portfolio"] });
    },
  });
}

export function useSetApprovalOverride() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, approved }: { id: number; approved: boolean }) =>
      api.post(`/support-queue/${id}/approval-override`, { approved }).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["support-queue"] }),
  });
}

/** Decline a small enhancement — closed as declined, stays closed across syncs. */
export function useDeclineSupportQueueItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.post(`/support-queue/${id}/decline`).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["support-queue"] }),
  });
}

/** 'Send to Enhancements' on a board request (2026-10-01). */
export function useSendProjectToEnhancements() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (projectId: string) => api.post(`/support-queue/from-project/${projectId}`).then((r) => r.data as { item_id: number; subject: string }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["support-queue"] });
      qc.invalidateQueries({ queryKey: ["portfolio"] });
      qc.invalidateQueries({ queryKey: ["forecast"] });
    },
  });
}
